import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from "react-redux";
import { MdClose } from 'react-icons/md';
import { useUser } from '../../../hooks/useUser';
import '../LikesPanel/LikesPanel.scss';
import { useNavigate } from 'react-router-dom';

const UsersPanel = ({ isOpen, onClose, userId, userCount, type }) => {
    const [page, setPage] = useState(1);
    const currentUserId = useSelector((state) => state.projectInfo.uid);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [displayedUsers, setDisplayedUsers] = useState([]);
    const { users, handleUserFollowUnfollow, fetchFollowersAndFollowingData } = useUser();
    const navigate = useNavigate();
    const containerRef = useRef(null);
    const loadingRef = useRef(false); // Add this to prevent double calls
    const initialLoadDone = useRef(false); // Track if initial load is done

    const loadMoreUsers = useCallback(async (currentPage = page) => {
        // Prevent multiple simultaneous calls
        if (loadingRef.current || loading) return;

        loadingRef.current = true;
        setLoading(true);

        try {
            const result = await fetchFollowersAndFollowingData(userId, currentPage, type);
            if (result && result.length > 0) {
                setDisplayedUsers(prev => {
                    // Check for duplicates to prevent adding same users
                    const existingUserIds = prev.map(u => u.user_id);
                    const newUsers = result.filter(user => !existingUserIds.includes(user.user_id));
                    const updatedUsers = [...prev, ...newUsers];

                    // Update hasMore based on total count
                    setHasMore(updatedUsers.length < userCount);
                    return updatedUsers;
                });

                setPage(currentPage + 1);
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Error loading more users:', error);
            setHasMore(false);
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }, [userId, type, userCount, fetchFollowersAndFollowingData]);

    const onFollowUnfollowClick = useCallback(async (userId, currentUserId, isFollowed) => {
        await handleUserFollowUnfollow(userId, currentUserId, isFollowed);
        setDisplayedUsers(prevUsers => prevUsers.map(user =>
            user.user_id === userId ? { ...user, is_following: JSON.stringify(!isFollowed) } : user
        ));
    }, [handleUserFollowUnfollow]);

    // Reset panel and load initial users when opening
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';

            // Reset all state
            setPage(1);
            setHasMore(userCount > 0);
            setDisplayedUsers([]);
            setLoading(false);
            loadingRef.current = false;
            initialLoadDone.current = false;

            // Load initial users if there are any
            setTimeout(() => {
                loadMoreUsers();
            }, 0);
            
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('likes-panel-overlay')) {
            onClose();
        }
    };

    const navigateToProfile = (userId) => {
        navigate(`/community/profiles/${userId || "unknown"}`);
        onClose();
    };

    const handleLoadMore = () => {
        if (!loadingRef.current && !loading && hasMore) {
            loadMoreUsers(page);
        }
    };

    return (
        <>
            <div className="likes-panel-backdrop fade-in"></div>
            <div className="likes-panel-overlay" onClick={handleOverlayClick}>
                <div className="likes-panel modal-animation">
                    <div className="likes-panel-header">
                        <h2>Users</h2>
                        <button onClick={onClose} className="close-button-like hover-grow">
                            <MdClose size={24} />
                        </button>
                    </div>
                    <div className="likes-panel-content" ref={containerRef}>
                        {userCount === 0 ? (
                            <div className="no-users-panel">
                                <h3 className="no-users-title">No Users Found</h3>
                                <p className="no-users-message">There are no users to display here.</p>
                            </div>
                        ) : (
                            <>
                                {displayedUsers.map((userData) => {
                                    const isFollowing = JSON.parse(userData?.is_following) || false;
                                    return (
                                        <div key={userData.user_id} className="liked-user-item stagger-item">
                                            <img
                                                src={userData?.user_image || "https://gizmeon.mdc.akamaized.net/PUB-50119/post/1749720612786.png"}
                                                alt={userData?.user_name}
                                                className="user-profile-pic hover-grow"
                                                onClick={() => navigateToProfile(userData.user_id)}
                                            />
                                            <div className="user-info">
                                                <span className="user-name">{userData?.user_name || "Unknown User"}</span>
                                                <span className="user-full-name">{userData?.fullName || ""}</span>
                                            </div>
                                            <button
                                                className={`follow-button ${isFollowing ? 'following' : ''} hover-brighten ripple`}
                                                hidden={userData.user_id === Number(currentUserId)}
                                                onClick={() => { onFollowUnfollowClick(userData.user_id, currentUserId, isFollowing) }}
                                            >
                                                {isFollowing ? "Unfollow" : "Follow"}
                                            </button>
                                        </div>
                                    );
                                })}
                                {(userCount > 0 && hasMore && !loading) && (
                                    <div className="load-more-container" onClick={handleLoadMore}>
                                        Load More
                                    </div>
                                )}
                                {loading && <div className="loading">Loading...</div>}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default UsersPanel;